import { createAccessControl } from "better-auth/plugins/access";

/**
 * Access Control Configuration for Purple Venture Studios
 * Reference: docs/auth.md, SRS Section 7 (Access Control & RBAC)
 *
 * Defines resources, actions, and role-based permissions for the platform's
 * three-tier RBAC system: admin, investor (Capital Partner), founder (Startup Founder)
 */

// Define all resources and their available actions
export const statement = {
  // User management - includes Better Auth's default admin actions
  user: [
    "create",
    "list",
    "read",
    "update",
    "delete",
    "set-role",
    "ban",
    "impersonate",
    "set-password",
  ],

  // Session management - Better Auth's default
  session: ["list", "revoke", "delete"],

  // Application review
  application: ["read", "approve", "reject"],

  // Stage upgrade requests
  stageUpgrade: ["create", "read", "approve", "reject"],

  // Startup profiles
  startup: ["read", "update"],

  // Investor profiles
  investor: ["read", "update"],

  // Deal flow (startup discovery for investors)
  dealFlow: ["read"],

  // Investor directory (for founders)
  investorDirectory: ["read"],

  // Interest expression
  interest: ["create", "read"],

  // Data requests
  dataRequest: ["create", "read", "approve", "decline"],

  // Purplemail (platform messaging)
  purplemail: ["create", "read", "send", "monitor"],

  // Materials (pitch decks, financials, etc.)
  materials: ["create", "read", "update", "delete", "share"],

  // Fundability scoring
  fundability: ["read", "calculate"],

  // Scorecard ratings
  scorecard: ["create", "read"],

  // Subscription management
  subscription: ["read", "update"],

  // Pipeline management (for investors)
  pipeline: ["create", "read", "update", "delete"],

  // Platform analytics
  platform: ["read"],
} as const;

// Create access controller
export const ac = createAccessControl(statement);

/**
 * Admin Role
 * Full platform control for platform administrators
 * Responsibilities: User management, application review, platform oversight, support
 */
export const admin = ac.newRole({
  // User management - full control (includes Better Auth's built-in actions)
  user: [
    "create",
    "list",
    "read",
    "update",
    "delete",
    "set-role",
    "ban",
    "impersonate",
    "set-password",
  ],

  // Session management
  session: ["list", "revoke", "delete"],

  // Application and stage upgrade review
  application: ["read", "approve", "reject"],
  stageUpgrade: ["read", "approve", "reject"],

  // Platform oversight - read-only access to all data
  dealFlow: ["read"],
  investorDirectory: ["read"],
  startup: ["read"],
  investor: ["read"],

  // Purplemail monitoring (for support and compliance)
  purplemail: ["read", "monitor"],

  // Materials access (for compliance and support)
  materials: ["read"],

  // Fundability and scorecards (for platform insights)
  fundability: ["read", "calculate"],
  scorecard: ["read"],

  // Subscription management
  subscription: ["read"],

  // Platform analytics
  platform: ["read"],
});

/**
 * Investor Role (Capital Partner)
 * Discovery, evaluation, and deal management
 * Responsibilities: Browse startups, express interest, evaluate deals, manage pipeline
 *
 * Note: Row-level filtering required:
 * - Can only update their own investor profile
 * - Can only see approved materials (enforce at query level)
 * - Pipeline is private to each investor
 */
export const investor = ac.newRole({
  // Deal discovery
  dealFlow: ["read"],
  startup: ["read"],

  // Own profile management (row-level check: userId === investor.userId)
  investor: ["read", "update"],

  // Interest and data requests
  interest: ["create", "read"],
  dataRequest: ["create", "read"],

  // Communication
  purplemail: ["create", "read", "send"],

  // Materials access (approved only - enforce at query level)
  materials: ["read"],

  // Evaluation tools
  fundability: ["read"],
  scorecard: ["create", "read"],

  // Subscription management
  subscription: ["read", "update"],

  // Pipeline management (private to investor)
  pipeline: ["create", "read", "update", "delete"],
});

/**
 * Founder Role (Startup Founder)
 * Profile management, materials, and investor engagement
 * Responsibilities: Manage startup profile, upload materials, respond to investors, discover investors
 *
 * Note: Row-level filtering required:
 * - Can only update their own startup profile and materials
 * - Stage upgrade requests only for their own startup
 * - Data request approval only for requests to them
 */
export const founder = ac.newRole({
  // Investor discovery
  investorDirectory: ["read"],
  investor: ["read"],

  // Own startup profile management (row-level check: userId === startup.founderId)
  startup: ["read", "update"],

  // Interest expression
  interest: ["create", "read"],

  // Data request handling (can approve/decline requests to them)
  dataRequest: ["read", "approve", "decline"],

  // Communication
  purplemail: ["create", "read", "send"],

  // Materials management (own startup only)
  materials: ["create", "read", "update", "delete", "share"],

  // Evaluation tools
  fundability: ["read"],
  scorecard: ["create", "read"],

  // Subscription management
  subscription: ["read", "update"],

  // Stage upgrade requests (own startup only)
  stageUpgrade: ["create", "read"],
});

/**
 * Type exports for TypeScript inference
 */
export type Role = "admin" | "investor" | "founder";
export type Statement = typeof statement;
export type AccessControl = typeof ac;
